import { Box, BoxProps } from '@chakra-ui/react'
import { forwardRef } from 'react'

type ScrollDirection = 'auto' | 'scroll' | 'hidden'

export const defaultScrollConfig = {
  '&::-webkit-scrollbar': {
    display: 'block',
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
    },
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
} as const

export interface ScrollerProps extends Omit<BoxProps, 'overflowX' | 'overflowY'> {
  horizontal?: ScrollDirection
  vertical?: ScrollDirection
  /**
   * Position of the scrollbar. Default is 'right'.
   * When set to 'left', the scrollbar will appear on the left side of the container.
   */
  scrollbarPosition?: 'left' | 'right'
}

export const Scroller = forwardRef<HTMLDivElement, ScrollerProps>(
  (
    {
      children,
      horizontal = 'hidden',
      vertical = 'auto',
      scrollbarPosition = 'right',
      sx,
      ...boxProps
    },
    ref
  ) => {
    const isScrollbarOnLeft = scrollbarPosition === 'left'

    return (
      <Box
        ref={ref}
        overflowY={vertical}
        overflowX={horizontal}
        sx={{
          ...defaultScrollConfig,
          ...(isScrollbarOnLeft && { direction: 'rtl' }),
          ...sx,
        }}
        {...boxProps}
      >
        {isScrollbarOnLeft ? (
          <Box sx={{ direction: 'ltr' }} width="100%" height="100%">
            {children}
          </Box>
        ) : (
          children
        )}
      </Box>
    )
  }
)

Scroller.displayName = 'Scroller'
